/** Time-ago label that refreshes each minute; tooltip shows the absolute time. Kelly-local so the surface
 *  never depends on ai-teammate internals. */

import { useSyncExternalStore } from "react"
import { Box, Tooltip } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import moment from "moment"

const ONE_MINUTE_MS = 60_000
const TIME_SX = { color: "var(--app-text-secondary)", fontSize: "0.75rem", whiteSpace: "nowrap", alignSelf: "center" } satisfies SxProps<Theme>

// ONE shared minute ticker for every MessageTime: a long thread renders hundreds of rows, and a per-row
// setInterval would be hundreds of timers. Subscribers re-render via useSyncExternalStore; the interval runs
// only while at least one row is mounted, and stops once the last unsubscribes.
const tickSubscribers = new Set<() => void>()
let tickHandle: ReturnType<typeof setInterval> | null = null
let tickVersion = 0

function subscribeToMinuteTick(onTick: () => void): () => void {
  tickSubscribers.add(onTick)
  if (tickHandle === null) {
    tickHandle = setInterval(() => {
      tickVersion += 1
      for (const notify of tickSubscribers) notify()
    }, ONE_MINUTE_MS)
  }
  return () => {
    tickSubscribers.delete(onTick)
    if (tickSubscribers.size === 0 && tickHandle !== null) {
      clearInterval(tickHandle)
      tickHandle = null
    }
  }
}

// A monotonic version number: a stable snapshot between ticks (useSyncExternalStore re-renders only when it changes).
const getTickSnapshot = () => tickVersion

export function MessageTime({ date }: { date: string }) {
  useSyncExternalStore(subscribeToMinuteTick, getTickSnapshot, getTickSnapshot)
  const parsed = moment(date)
  // A malformed/empty createdAt would render a literal "Invalid date" (and trip moment's deprecation path) — skip it.
  if (!parsed.isValid()) return null
  return (
    <Tooltip title={parsed.format("LLL")}>
      <Box component="span" sx={TIME_SX} data-testid="chatbox-message-time">{parsed.fromNow()}</Box>
    </Tooltip>
  )
}
